//
// Copyright (c) 2011-2020 by Curalate, Inc.
//
import { PojoMap } from 'pojo-maps';

import { InfluencersListAction } from '@influencers/actions/influencersListActions';
import {
  INFLUENCER_RIGHTS_SETTINGS,
  INFLUENCERS_LIST_ACTIONS,
} from '@influencers/constants/influencersConstants';
import { IInfluencersListState } from '@influencers/models/influencersStateModel';

const initialState: IInfluencersListState = {
  lists: undefined,
  createdList: undefined,
  addToListErrors: [],
  displayedListUsers: [],
  listUsersCount: undefined,
  listUsers: [],
  listsByUser: PojoMap.empty(),
  rightsSetting: INFLUENCER_RIGHTS_SETTINGS.DEFAULT,
  requestingDisplayedListUsers: false,
  requestingListUsers: false,
  requestingAddUserToList: new Set(),
  requestingRemoveUserFromList: new Set(),
  requestingCreateList: false,
  requestingLists: false,
  requestingDeleteList: false,
  requestingListsByUser: new Set(),
  requestingRightsSetting: false,
  requestingRightsSettingUpdate: false,
  hasSaveRightsSettingError: false,
  trackedInfluencers: {
    limit: undefined,
    users: undefined,
    requestingTrackedInfluencers: false,
  },
  bulkAdd: {
    isComplete: undefined,
    results: undefined,
    statusCode: undefined,
    listStatusCacheId: undefined,
    requestingBulkAdd: false,
    requestingBulkAddStatus: false,
  },
  updateListName: {
    errors: {
      updateListNameError: undefined,
    },
    isUpdatingListName: false,
    listNameDraft: '',
  },
};

export default function InfluencersListReducer(
  state: IInfluencersListState = initialState,
  action: InfluencersListAction,
): IInfluencersListState {
  switch (action.type) {
    case INFLUENCERS_LIST_ACTIONS.RECEIVE_ADDED_USER_TO_LIST: {
      const userId = action.user.userId;
      let newList = {
        name: undefined,
        id: action.listId,
      };
      if (state.lists) {
        const matchingLists = state.lists.filter((l) => l.id === action.listId);
        if (matchingLists.length > 0) {
          newList = matchingLists[0];
        }
      }
      const clonedRequestingAddUserToList = new Set(
        state.requestingAddUserToList,
      );
      clonedRequestingAddUserToList.delete(action.listId);

      const userList = PojoMap.get(state.listsByUser, userId) ?? [];
      return {
        ...state,
        requestingAddUserToList: clonedRequestingAddUserToList,
        listsByUser: PojoMap.set(
          state.listsByUser,
          userId,
          userList.concat(newList),
        ),
      };
    }
    case INFLUENCERS_LIST_ACTIONS.RECEIVE_ADDED_USER_TO_LIST_ERROR: {
      if (state.addToListErrors === undefined) {
        state.addToListErrors = [];
      }
      state.addToListErrors.push(action.error);
      const clonedRequestingAddUserToList = new Set(
        state.requestingAddUserToList,
      );
      clonedRequestingAddUserToList.delete(action.error.listId);

      return {
        ...state,
        requestingAddUserToList: clonedRequestingAddUserToList,
      };
    }
    case INFLUENCERS_LIST_ACTIONS.CLEAR_CREATE_LIST: {
      return {
        ...state,
        createdList: undefined,
      };
    }
    case INFLUENCERS_LIST_ACTIONS.RECEIVE_CREATED_LIST: {
      return {
        ...state,
        createdList: action.list,
        requestingCreateList: false,
      };
    }
    case INFLUENCERS_LIST_ACTIONS.RECEIVE_SAVED_LISTS: {
      return {
        ...state,
        lists: action.lists,
        requestingLists: false,
      };
    }
    case INFLUENCERS_LIST_ACTIONS.RECEIVE_SAVED_LISTS_BY_USER: {
      const clonedRequestingListsByUser = new Set(state.requestingListsByUser);
      clonedRequestingListsByUser.delete(action.userId);
      return {
        ...state,
        requestingListsByUser: clonedRequestingListsByUser,
        listsByUser: PojoMap.set(
          state.listsByUser,
          action.userId,
          action.lists,
        ),
      };
    }
    case INFLUENCERS_LIST_ACTIONS.REQUEST_SAVED_LISTS: {
      return {
        ...state,
        requestingLists: true,
      };
    }
    case INFLUENCERS_LIST_ACTIONS.REQUEST_SAVED_LISTS_BY_USER: {
      const clonedRequestingListsByUser = new Set(state.requestingListsByUser);
      clonedRequestingListsByUser.add(action.userId);
      return {
        ...state,
        requestingListsByUser: clonedRequestingListsByUser,
      };
    }
    case INFLUENCERS_LIST_ACTIONS.REQUEST_ADD_USER_TO_LIST: {
      const clonedRequestingAddUserToList = new Set(
        state.requestingAddUserToList,
      );
      clonedRequestingAddUserToList.add(action.listId);
      return {
        ...state,
        requestingAddUserToList: clonedRequestingAddUserToList,
      };
    }
    case INFLUENCERS_LIST_ACTIONS.REQUEST_REMOVE_USER_FROM_LIST: {
      const clonedRequestingRemoveUserFromList = new Set(
        state.requestingRemoveUserFromList,
      );
      clonedRequestingRemoveUserFromList.add(action.listId);
      return {
        ...state,
        requestingRemoveUserFromList: clonedRequestingRemoveUserFromList,
      };
    }
    case INFLUENCERS_LIST_ACTIONS.RECEIVE_REMOVED_USER_FROM_LIST: {
      const listUsers = state.listUsers
        ? state.listUsers.filter((user) => {
            return !(user.userId === action.user.userId);
          })
        : undefined;
      const displayedListUsers = state.displayedListUsers
        ? state.displayedListUsers.filter((user) => {
            return !(user.userId === action.user.userId);
          })
        : undefined;
      const listUsersCount = state.listUsersCount
        ? state.listUsersCount - 1
        : undefined;

      const clonedRequestingRemoveUserFromList = new Set(
        state.requestingRemoveUserFromList,
      );
      clonedRequestingRemoveUserFromList.delete(action.listId);
      return {
        ...state,
        listUsers,
        displayedListUsers,
        listUsersCount,
        requestingRemoveUserFromList: clonedRequestingRemoveUserFromList,
      };
    }
    case INFLUENCERS_LIST_ACTIONS.REQUEST_CREATE_LIST: {
      return {
        ...state,
        requestingCreateList: true,
      };
    }
    case INFLUENCERS_LIST_ACTIONS.REQUEST_TRACKED_INFLUENCERS: {
      return {
        ...state,
        trackedInfluencers: {
          users: undefined,
          limit: undefined,
          requestingTrackedInfluencers: true,
        },
      };
    }
    case INFLUENCERS_LIST_ACTIONS.RECEIVE_TRACKED_INFLUENCERS: {
      return {
        ...state,
        trackedInfluencers: {
          users: action.users,
          limit: action.limit,
          requestingTrackedInfluencers: false,
        },
      };
    }
    case INFLUENCERS_LIST_ACTIONS.ADD_TRACKED_INFLUENCER: {
      return {
        ...state,
        trackedInfluencers: {
          ...state.trackedInfluencers,
          users: [...state.trackedInfluencers.users, action.user],
        },
      };
    }
    case INFLUENCERS_LIST_ACTIONS.UPDATE_TRACKED_INFLUENCERS: {
      return {
        ...state,
        trackedInfluencers: {
          ...state.trackedInfluencers,
          users: action.users,
        },
      };
    }
    case INFLUENCERS_LIST_ACTIONS.UPDATE_INFLUENCER_LISTS: {
      return {
        ...state,
        lists: action.lists,
      };
    }
    default:
      return state;
  }
}
